"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
export default function Quote() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const collageImages = [{
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/precision_die_cutting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/precision_die_cutting.png",
    sizes: "100vw",
    alt: "Precision die-cutting machine in operation",
    rotation: "-rotate-3",
    offset: "ml-0 mt-0",
    width: "w-[78%]",
    z: "z-10"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/industrial_slitting_process.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/industrial_slitting_process.webp",
    sizes: "100vw",
    alt: "Industrial lathe slitting process for films",
    rotation: "rotate-2",
    offset: "ml-auto -mt-10",
    width: "w-[82%]",
    z: "z-20"
  }, {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/advanced_laminating_system.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723303/img/advanced_laminating_system.png",
    sizes: "100vw",
    alt: "Advanced multi-layer laminating system",
    rotation: "-rotate-1",
    offset: "ml-6 -mt-8",
    width: "w-[74%]",
    z: "z-10"
  }];
  return <section data-section-id="514" id="request-technical-quote" className="relative w-full bg-background overflow-hidden dark bg-background text-foreground">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-20 md:py-28 lg:min-h-[80vh] flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 w-full items-center">
          {}
          <div ref={leftRef} className={`lg:col-span-6 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-8 bg-foreground/60" aria-hidden="true" />
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-foreground/70">
                Inquiry
              </span>
            </div>

            {}
            <h1 className="font-default font-bold text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-[6.25rem] leading-[0.95] tracking-tighter">
              Precision Converting <span className="italic font-light">Solutions.</span>
            </h1>

            {}
            <p className="mt-8 text-xl leading-relaxed text-muted-foreground max-w-md font-default">
              GST NA, INC. delivers customized die-cutting, slitting, and laminating services for industrial manufacturers across North America.
            </p>

            {}
            <div className="mt-10 rounded-lg border border-border bg-card p-6 shadow-md">
              <div className="flex items-center gap-3 mb-5">
                <span className="h-px w-6 bg-primary" aria-hidden="true" />
                <span className="font-mono text-[0.7rem] tracking-[0.2em] uppercase text-foreground/70">
                  Request a Quote
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="process" className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">
                    Process
                  </Label>
                  <Select>
                    <SelectTrigger id="process" className="w-full h-11 bg-background border-border rounded-md">
                      <SelectValue placeholder="Select process" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="die-cutting">Die-Cutting</SelectItem>
                      <SelectItem value="slitting">Slitting</SelectItem>
                      <SelectItem value="laminating">Laminating</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="material" className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">
                    Material
                  </Label>
                  <Select>
                    <SelectTrigger id="material" className="w-full h-11 bg-background border-border rounded-md">
                      <SelectValue placeholder="Select material" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adhesives">Adhesives</SelectItem>
                      <SelectItem value="films">Films</SelectItem>
                      <SelectItem value="gaskets">Gaskets</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="industry" className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground">
                    Industry
                  </Label>
                  <Select>
                    <SelectTrigger id="industry" className="w-full h-11 bg-background border-border rounded-md">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="electronics">Electronics</SelectItem>
                      <SelectItem value="automotive">Automotive</SelectItem>
                      <SelectItem value="aerospace">Aerospace</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex flex-col gap-2">
                  <span className="font-mono text-[0.65rem] tracking-[0.18em] uppercase text-muted-foreground invisible" aria-hidden="true">
                    Action
                  </span>
                  <Link className="h-11 w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-lg font-medium group underline cursor-pointer" href="https://mail to: rkim@gstna.com" newTab><Button type="button">
                    Submit
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </Button></Link>
                </div>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`lg:col-span-6 relative transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="absolute inset-0 -z-0 opacity-30" aria-hidden="true" style={{
            backgroundImage: "radial-gradient(circle, var(--primary) 1px, transparent 1.5px)",
            backgroundSize: "22px 22px"
          }} />

            <div className="relative mx-auto max-w-md lg:max-w-lg">
              {collageImages.map((image, i) => <div key={i} data-index={i} className={`relative ${image.width} ${image.offset} ${image.rotation} ${image.z} bg-card p-3 pb-12 shadow-lg rounded-lg transition-transform duration-500 hover:rotate-0 hover:scale-[1.02]`}>
                  <div className="overflow-hidden bg-muted aspect-[4/5]">
                    <img src={image.src} alt={image.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}